/* eslint-disable react/jsx-no-useless-fragment */
import { useRouter } from 'next/router';
import type { FC, PropsWithChildren } from 'react';
import { useEffect } from 'react';

import { useProfile } from '@/hooks/useProfile';
import { EnumAccountType } from '@/services/auth/auth';

import CandidateWrongZone from './CandidateWrongZone';
import LoadingPlaceholder from './LoadingPlaceholder';

export const whitelistedPaths = [
  '/dashboard/auth/signin',
  '/dashboard/auth/signup',
  '/dashboard/auth/register/activate',
  '/dashboard/auth/reset-password',
  '/dashboard/auth/request-reset-password',
  '/dashboard/pricing',
  '/academy',
  '/jobs',
  '/jobs/[id]',
  '/',
  '/bullet-points',
  '/job-matching',
  '/resume',
  '/about-us',
  '/recruiter',
  '/company-profile',
  '/company-profile/[id]',
  '/candidate-auth/password-reset',
  '/candidate-auth/activate',
  '/candidate-auth/signin',
  '/candidate-auth/signup',
  '/pricing/candidate',
  '/pricing/recruiter',
  '/community',
  '/community/[id]',
  '/profile-complete',
  '/account/delete-confirm',
  '/hr-bots',
  '/ats',
  '/contact-us',
  '/resume-analysis',
  '/resume-checker',
  '/resume-checker/successfully-booked-consultant',
  '/pdf-render',
  '/sitemaps/jobs/[chunk]',
  '/sitemaps/company-profile.xml',
  '/sitemaps/blog.xml',
  '/career-guides',
  '/career-guides/[slug]',
  '/manufacturing-blog',
  '/manufacturing-blog/[slug]',
  '/blog',
  '/blog/[slug]',
  '/privacy-policy',
  '/terms-of-service',
  '/pricing/customer-portal',
  '/tests',
  '/tests/[uuid]',
  '/AIHR',
  '/ai-company',
  '/ai-company/[slug]',
  '/press-room',
  '/press-room/[slug]',
  '/success-stories',
  '/success-stories/pegasus-solar',
  '/success-stories/junction',
  '/success-stories/pulsar',
  '/ai-blue-collar',
  '/services',
  '/cnc-rfq',
  '/industry/[industrySlug]',
  '/industry/[industrySlug]/[verticalSlug]',
  '/case-studies',
  '/case-studies/pegasus',

  '/sms-terms',
  '/cookie-policy',
  '/responsible-disclosure',
];

const AuthOnlyPage: FC<PropsWithChildren<{}>> = ({ children }) => {
  const router = useRouter();
  const { data: profileData, isLoading } = useProfile({
    refetchOnWindowFocus: false,
  });

  const isWhitelistedPath = whitelistedPaths.includes(router.pathname);
  const isDashboardRoute = router.pathname.startsWith('/dashboard');
  const isDarkLoaderRoute =
    isDashboardRoute || router.pathname.startsWith('/pricing/recruiter');

  const isCandidate = profileData?.accountType === EnumAccountType.CANDIDATE;
  const isAdmin = profileData?.accountType === EnumAccountType.ADMIN;
  const isDemoCompetencyAssessment =
    profileData?.accountType === EnumAccountType.DEMO_COMPETENCY_ASSESSMENT;
  const isCompetencyAssessmentRoute = router.pathname.startsWith(
    '/competency-assessment'
  );

  const shouldRedirectToDashboardAuth =
    !isWhitelistedPath && !profileData && !isLoading && isDashboardRoute;
  const shouldRedirectToCandidateAuth =
    !isWhitelistedPath && !profileData && !isLoading && !isDashboardRoute;
  const candidateOnDashboard =
    !!profileData && isCandidate && isDashboardRoute && !isWhitelistedPath;
  const demoUserOutsideCompetencyAssessment =
    !!profileData &&
    isDemoCompetencyAssessment &&
    !isCompetencyAssessmentRoute &&
    !isWhitelistedPath;
  const nonAllowedRoleOnCompetencyAssessment =
    !!profileData &&
    isCompetencyAssessmentRoute &&
    !isDemoCompetencyAssessment &&
    !isAdmin;

  useEffect(() => {
    if (shouldRedirectToDashboardAuth) {
      router.replace({
        pathname: '/dashboard/auth/signin',
        query: { callback: router.asPath },
      });
    }
  }, [shouldRedirectToDashboardAuth]);

  useEffect(() => {
    if (shouldRedirectToCandidateAuth) {
      router.replace({
        pathname: '/candidate-auth/signin',
        query: { callback: router.asPath },
      });
    }
  }, [shouldRedirectToCandidateAuth]);

  useEffect(() => {
    if (demoUserOutsideCompetencyAssessment) {
      router.replace('/competency-assessment');
    }
  }, [demoUserOutsideCompetencyAssessment]);

  useEffect(() => {
    if (nonAllowedRoleOnCompetencyAssessment) {
      router.replace('/');
    }
  }, [nonAllowedRoleOnCompetencyAssessment]);

  if (candidateOnDashboard) return <CandidateWrongZone />;
  if (
    demoUserOutsideCompetencyAssessment ||
    nonAllowedRoleOnCompetencyAssessment
  ) {
    return <LoadingPlaceholder theme={isDarkLoaderRoute ? 'dark' : 'light'} />;
  }
  if (isWhitelistedPath || profileData) return <>{children}</>;
  return <LoadingPlaceholder theme={isDarkLoaderRoute ? 'dark' : 'light'} />;
};

export default AuthOnlyPage;
